
import { useTranslation } from "react-i18next";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";

interface PrivacyPolicyProps {
  triggerLabel?: string;
  className?: string;
}

const PrivacyPolicy = ({ triggerLabel, className = "" }: PrivacyPolicyProps) => {
  const { t } = useTranslation();

  return (
    <Dialog>
      <DialogTrigger asChild>
        <Button variant="link" className={`text-sm text-gray-500 hover:text-gray-700 p-0 h-auto ${className}`}>
          {triggerLabel || t('common.privacy')}
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-md max-h-[80vh] overflow-auto">
        <DialogHeader>
          <DialogTitle className="text-xl font-bold text-[#006B5F]">
            {t('privacy.title')}
          </DialogTitle>
          <DialogDescription className="text-base text-gray-800">
            {t('privacy.subtitle')}
          </DialogDescription>
        </DialogHeader>
        
        <div className="mt-4 space-y-4 text-gray-700">
          <p>
            {t('privacy.intro')}
          </p>

          <div className="bg-gray-50 p-3 rounded-md border border-gray-200">
            <p className="text-sm text-gray-700">
              {t('privacy.company')}
            </p>
          </div>

          <div>
            <h3 className="font-bold text-[#006B5F] mt-4 mb-2">{t('privacy.cookies.title')}</h3>
            <p>
              {t('privacy.cookies.content')}
            </p>
          </div>

          <div>
            <h3 className="font-bold text-[#006B5F] mt-4 mb-2">{t('privacy.analytics.title')}</h3>
            <p>
              {t('privacy.analytics.content')}
            </p>
            <p className="mt-2 text-sm text-gray-600">
              {t('privacy.analytics.retention')}
            </p>
          </div>

          <div>
            <h3 className="font-bold text-[#006B5F] mt-4 mb-2">{t('privacy.dataCollection.title')}</h3>
            <p>{t('privacy.dataCollection.intro')}</p>
            <ul className="list-disc pl-6 mt-2 space-y-1">
              {t('privacy.dataCollection.items', { returnObjects: true }).map((item: string, index: number) => (
                <li key={index}>{item}</li>
              ))}
            </ul>
          </div>

          <div>
            <h3 className="font-bold text-[#006B5F] mt-4 mb-2">{t('privacy.dataUsage.title')}</h3>
            <p>{t('privacy.dataUsage.intro')}</p>
            <ul className="list-disc pl-6 mt-2 space-y-1">
              {t('privacy.dataUsage.items', { returnObjects: true }).map((item: string, index: number) => (
                <li key={index}>{item}</li>
              ))}
            </ul>
            <p className="mt-2">
              {t('privacy.dataUsage.sharing')}
            </p>
          </div>
          
          <div>
            <h3 className="font-bold text-[#006B5F] mt-4 mb-2">{t('privacy.retention.title')}</h3>
            <p>
              {t('privacy.retention.content')}
            </p>
          </div>

          <div>
            <h3 className="font-bold text-[#006B5F] mt-4 mb-2">{t('privacy.rights.title')}</h3>
            <p>{t('privacy.rights.intro')}</p>
            <ul className="list-disc pl-6 mt-2 space-y-1">
              {t('privacy.rights.items', { returnObjects: true }).map((item: string, index: number) => (
                <li key={index}>{item}</li>
              ))}
            </ul>
            <p className="mt-2">
              {t('privacy.rights.contact').split('info@omsorgsterapi.dk')[0]}
              <a
                href="mailto:info@omsorgsterapi.dk"
                className="text-[#006B5F] hover:underline"
              >
                info@omsorgsterapi.dk
              </a>
              {t('privacy.rights.contact').split('info@omsorgsterapi.dk')[1]}
            </p>
          </div>

          <div>
            <h3 className="font-bold text-[#006B5F] mt-4 mb-2">{t('privacy.security.title')}</h3>
            <p>
              {t('privacy.security.content')}
            </p>
          </div>

          <div>
            <h3 className="font-bold text-[#006B5F] mt-4 mb-2">{t('privacy.thirdParty.title')}</h3>
            <p className="mb-2">
              {t('privacy.thirdParty.content')}
            </p>
            <ul className="list-disc pl-6 mt-2 space-y-2">
              <li>
                <strong>Clerk:</strong> {t('privacy.thirdParty.services.clerk')}
              </li>
              <li>
                <strong>Stripe:</strong> {t('privacy.thirdParty.services.stripe')}
              </li>
              <li>
                <strong>Cal.com:</strong> {t('privacy.thirdParty.services.calcom')}
              </li>
              <li>
                <strong>Email:</strong> {t('privacy.thirdParty.services.email')}
              </li>
            </ul>
            <p className="mt-3 text-sm text-gray-600">
              {t('privacy.thirdParty.link')}
            </p>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default PrivacyPolicy;
